import React, { createContext, useCallback, useContext, useMemo, useRef } from 'react';
import { AnalyticsContext } from '@atlaskit/analytics-next';
const AnalyticsPropertiesContext = /*#__PURE__*/createContext({
  propertiesRef: {
    current: {
      conversationChannelId: '',
      experienceId: '',
      product: '',
      agentId: null,
      fullScreen: false
    }
  },
  setAnalyticsPropertiesWithoutRerender: () => {}
});

/**
 * This context contains ref to the properties that will be added to `useAnalytics` when firing an event
 * Using `ref` so this context does not rerender when any of the analytics properties change
 * Because the updated attributes are only needed when the analytics is fired
 */
export const AnalyticsPropertiesContextProvider = ({
  children
}) => {
  const propertiesRef = useRef({
    conversationChannelId: '',
    experienceId: '',
    product: '',
    agentId: null,
    fullScreen: false
  });
  const setAnalyticsPropertiesWithoutRerender = useCallback(properties => {
    propertiesRef.current = {
      ...propertiesRef.current,
      ...properties
    };
  }, []);
  const value = useMemo(() => ({
    propertiesRef,
    setAnalyticsPropertiesWithoutRerender
  }), [setAnalyticsPropertiesWithoutRerender]);
  return /*#__PURE__*/React.createElement(AnalyticsPropertiesContext.Provider, {
    value: value
  }, children);
};

/**
 * Adding extra wrapper to AnalyticsPropertiesContextProvider
 * for `source` attribute
 * using AnalyticsContext
 */
export const AnalyticsContextWithSourceAndProperties = ({
  source,
  children
}) => {
  const data = useMemo(() => ({
    source
  }), [source]);
  return /*#__PURE__*/React.createElement(AnalyticsContext, {
    data: data
  }, /*#__PURE__*/React.createElement(AnalyticsPropertiesContextProvider, null, children));
};
export const useAnalyticsPropertiesContext = () => {
  return useContext(AnalyticsPropertiesContext);
};